import { AREA_CODE_BLOG_SLUGS } from "@/data/areaCodeBlogSlugs";
import { toCanonicalPath } from "@/lib/siteUrl";

export function getBlogSlugForAreaCode(code: string): string | undefined {
  return AREA_CODE_BLOG_SLUGS[code];
}

export function getBlogHrefForAreaCode(code: string): string | undefined {
  const slug = getBlogSlugForAreaCode(code);
  return slug ? toCanonicalPath(`/blogs/${slug}`) : undefined;
}

/** Visible / accessible label that describes the destination blog article. */
export function getBlogLinkLabelForAreaCode(code: string): string | undefined {
  if (!getBlogSlugForAreaCode(code)) {
    return undefined;
  }

  return `${code} area code`;
}

/**
 * Prefer the area-code blog when one exists.
 * Only fall back to the region table when there is no blog for that code.
 */
export function getAreaCodeDestinationHref(
  code: string,
  regionSlug?: string,
): string {
  const blogHref = getBlogHrefForAreaCode(code);
  if (blogHref) {
    return blogHref;
  }

  if (regionSlug) {
    return toCanonicalPath(`/area-codes/${regionSlug}`);
  }

  return toCanonicalPath("/area-code-list");
}

export function extractAreaCodeFromHref(href: string): string | undefined {
  const blogMatch = href.match(/\/blogs\/(\d{3})-area-code-/);
  if (blogMatch) {
    return blogMatch[1];
  }

  const legacyMatch = href.match(/\/area-code\/(\d{3})\/?$/);
  if (legacyMatch) {
    return legacyMatch[1];
  }

  return undefined;
}

/**
 * Normalize in-article links so area-code references go to the blog (not the
 * region table) and use relateable anchor text for the destination article.
 */
export function resolveInternalBlogLink(
  anchor: string,
  href: string,
): { anchor: string; href: string } {
  const trimmedAnchor = anchor.trim();
  const codeFromAnchor = trimmedAnchor.match(/^(\d{3})(?:\s+area\s+code)?$/i)?.[1];
  const codeFromHref = extractAreaCodeFromHref(href);
  const regionOnlyHref = href.match(/^\/area-codes\/[^/]+\/?$/) !== null;

  // Bare/legacy area-code links, or anchors that are clearly a code, must not
  // land on the region table when a blog exists for that code.
  if (codeFromHref || (codeFromAnchor && regionOnlyHref)) {
    const code = codeFromHref ?? codeFromAnchor!;
    const blogHref = getBlogHrefForAreaCode(code);
    if (blogHref) {
      return {
        href: blogHref,
        anchor: getBlogLinkLabelForAreaCode(code) ?? `${code} area code`,
      };
    }
  }

  // Blog destinations should use relateable "NNN area code" wording.
  if (codeFromHref && href.includes("/blogs/")) {
    const label = getBlogLinkLabelForAreaCode(codeFromHref);
    if (label && !/area code/i.test(trimmedAnchor)) {
      return { href, anchor: label };
    }
  }

  return { anchor: trimmedAnchor, href };
}
